import { PROTOCOL_VERSION } from '@borgee/agent-remote-protocol';
import { validSessionAvailability, validTimelineRecoveryResult } from './session-observation.js';
import { encodeNormalizedTransport, isRecord, utf8Bytes } from './normalized-transport-codec.js';
import { NORMALIZED_FRAME_MAX_BYTES } from './normalized-transport-types.js';
export const NORMALIZED_APPLICATION_VERSION = 1;
export const NORMALIZED_APPLICATION_QUERY = 'agent_requests_version';
export const NORMALIZED_APPLICATION_HEADER = 'x-borgee-agent-requests-version';
function applicationFrame(logical, sequence) {
    return {
        type: 'agent_activity', shape: 'normalized', channel_id: logical.channel_id,
        ...(logical.task_id === undefined ? {} : { task_id: logical.task_id }),
        ...(logical.agent_id === undefined ? {} : { agent_id: logical.agent_id }),
        protocol_version: logical.protocol_version, sequence,
        event: { type: 'remote_transport', version: 1, frame: logical },
    };
}
function deliveryByteLength(frame) {
    // Go JSON fanout escapes HTML and line separators and replaces the producer identity.
    const json = JSON.stringify({ ...frame, agent_id: '<'.repeat(512) })
        .replace(/[<>&\u2028\u2029]/g, char => `\\u${char.charCodeAt(0).toString(16).padStart(4, '0')}`);
    return utf8Bytes(json).length;
}
/** Public event sequence belongs to the application; each physical envelope has its own sequence. */
export function encodeNormalizedApplicationTransport(frame, options) {
    const encoded = encodeNormalizedTransport(frame, {
        ...options,
        frameByteLength: logical => deliveryByteLength(applicationFrame(logical, Number.MAX_SAFE_INTEGER)),
    });
    if (encoded.status !== 'encoded')
        return encoded;
    const frames = [];
    for (const logical of encoded.frames) {
        const physical = applicationFrame(logical, options.nextSequence());
        if (!Number.isSafeInteger(physical.sequence) || physical.sequence <= 0 || deliveryByteLength(physical) > NORMALIZED_FRAME_MAX_BYTES)
            return { status: 'rejected', reason: 'frame_limit' };
        frames.push(physical);
    }
    return { ...encoded, frames };
}
/** The authenticated outer route is authoritative; duplicated application routes must agree. */
export function unwrapNormalizedApplicationTransport(input) {
    if (!isRecord(input) || input.type !== 'agent_activity' || input.shape !== 'normalized' || !isRecord(input.event) || input.event.type !== 'remote_transport')
        return input;
    if (typeof input.channel_id !== 'string' || !input.channel_id || utf8Bytes(input.channel_id).length > 256
        || input.task_id !== undefined && (typeof input.task_id !== 'string' || utf8Bytes(input.task_id).length > 256)
        || input.agent_id !== undefined && (typeof input.agent_id !== 'string' || !input.agent_id || utf8Bytes(input.agent_id).length > 512)
        || input.event.version !== NORMALIZED_APPLICATION_VERSION || !isRecord(input.event.frame) || !Number.isSafeInteger(input.sequence) || input.sequence <= 0)
        return undefined;
    const logical = input.event.frame;
    for (const key of ['channel_id', 'task_id']) {
        if ((logical[key] ?? '') !== (input[key] ?? ''))
            return undefined;
    }
    if (logical.agent_id !== undefined && logical.agent_id !== input.agent_id)
        return undefined;
    if (logical.type === 'normalized_session_availability') {
        const result = { ...logical, ...(input.agent_id === undefined ? {} : { agent_id: input.agent_id }) };
        return (input.protocol_version === PROTOCOL_VERSION || input.protocol_version === '1.5.0') && validSessionAvailability(result) ? result : undefined;
    }
    if (logical.type === 'normalized_activity_sync_result') {
        const result = { ...logical, ...(input.agent_id === undefined ? {} : { agent_id: input.agent_id }) };
        return (input.protocol_version === PROTOCOL_VERSION || input.protocol_version === '1.5.0') && validTimelineRecoveryResult(result) ? result : undefined;
    }
    if (logical.protocol_version !== input.protocol_version)
        return undefined;
    if (logical.type !== 'agent_activity' || !['normalized', 'normalized_fragment', 'normalized_timeline_replacement'].includes(String(logical.shape)))
        return undefined;
    return { ...logical, ...(input.agent_id === undefined ? {} : { agent_id: input.agent_id }) };
}
/** Provider recovery notifications travel on activity; request/reply receipts keep their RPC route. */
export function encodeNormalizedApplicationRecovery(frame, nextSequence) {
    if (!validTimelineRecoveryResult(frame) && !validSessionAvailability(frame))
        return undefined;
    const outer = {
        type: 'agent_activity', shape: 'normalized', channel_id: frame.channel_id,
        ...(frame.agent_id ? { agent_id: frame.agent_id } : {}),
        protocol_version: PROTOCOL_VERSION, sequence: nextSequence(),
        event: { type: 'remote_transport', version: 1, frame },
    };
    return Number.isSafeInteger(outer.sequence) && outer.sequence > 0 && deliveryByteLength(outer) <= NORMALIZED_FRAME_MAX_BYTES ? outer : undefined;
}
//# sourceMappingURL=normalized-application-transport.js.map